/**
 * Client-side vault cryptography for BarmetHub.
 *
 * Uses the real @barmetvault/crypto (libsodium) for all operations.
 * The master key is derived client-side via Argon2id and NEVER leaves
 * the browser — only the auth hash travels to the server.
 *
 * All binary data flows as Uint8Array. Base64 encoding/decoding
 * happens at the API boundary only.
 */

import {
  deriveMasterKey as sodiumDeriveMasterKey,
  deriveAuthHash as sodiumDeriveAuthHash,
  deriveKEK,
  deriveKEKWithSalt,
  encrypt,
  decrypt,
  generateDEK,
  generateKeyPair,
  encryptEnvelope,
  decryptEnvelope,
  zeroMemory,
  secureRandom,
  initializeCrypto,
} from '@barmetvault/crypto';

// ─── Base64 Helpers ────────────────────────────────────────────────────────

function toBase64(bytes: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

function fromBase64(b64: string): Uint8Array {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

// ─── Registration Crypto ───────────────────────────────────────────────────

export interface RegistrationCryptoPayload {
  masterPwdHash: string;        // base64
  kdfSalt: string;              // base64
  publicKeyEd25519: string;     // base64
  publicKeyX25519: string;      // base64
  privateKeysEnc: string;       // base64 — encrypted private keys blob
  privateKeysNonce: string;     // base64
  kekSalt: string;              // base64
  kdfParams: Record<string, number | string>;
}

/**
 * Perform all client-side crypto for user registration.
 *
 * 1. Derive the master key from the password via Argon2id
 * 2. Derive an auth hash (PBKDF2) to send to the server
 * 3. Generate Ed25519 + X25519 keypairs
 * 4. Encrypt the private keys with a KEK derived from the master key
 * 5. Return the registration payload
 *
 * The caller MUST zero the returned masterKey after caching it.
 */
export async function registerCrypto(
  email: string,
  masterPassword: string,
): Promise<{ payload: RegistrationCryptoPayload; masterKey: Uint8Array }> {
  await initializeCrypto();
  const normalizedEmail = email.toLowerCase().trim();

  // Generate random KDF salt
  const kdfSalt = secureRandom(16);

  // Derive master key (Argon2id) — stays on client only
  const masterKey = await sodiumDeriveMasterKey(masterPassword, kdfSalt);

  // Derive auth hash (PBKDF2) — sent to server for verification
  const authHash = await sodiumDeriveAuthHash(masterPassword, normalizedEmail);

  // Generate signing (Ed25519) and key exchange (X25519) keypairs
  const keyPair = generateKeyPair();

  // Derive KEK from master key to encrypt private keys
  const { key: kek, salt: kekSalt } = await deriveKEK(masterKey, 'private-keys');

  // Serialize private keys for encryption
  const privateKeysData = new Uint8Array([
    ...keyPair.signingPrivateKey,
    ...keyPair.encryptionPrivateKey,
  ]);

  // Encrypt private keys with KEK
  const encryptedPrivateKeys = await encrypt(privateKeysData, kek);

  // Zero the private keys copy
  zeroMemory(privateKeysData);
  zeroMemory(kek);

  const payload: RegistrationCryptoPayload = {
    masterPwdHash: toBase64(authHash),
    kdfSalt: toBase64(kdfSalt),
    publicKeyEd25519: toBase64(keyPair.signingPublicKey),
    publicKeyX25519: toBase64(keyPair.encryptionPublicKey),
    privateKeysEnc: toBase64(encryptedPrivateKeys.ciphertext),
    privateKeysNonce: toBase64(encryptedPrivateKeys.nonce),
    kekSalt: toBase64(kekSalt),
    kdfParams: {
      algorithm: 'PBKDF2-SHA256',
      iterations: 600000,
      keyBytes: 32,
    },
  };

  zeroMemory(authHash);

  return { payload, masterKey };
}

// ─── Step-Up Crypto (BHM-CDX-DIR-102) ─────────────────────────────────────

/**
 * Derive ONLY the auth hash for step-up reauthentication (POST /auth/step-up).
 * Same PBKDF2 derivation as login; the plaintext password never leaves this
 * function scope and the result is the client-side auth hash the server
 * compares against the stored masterPwdHash (zero-knowledge).
 */
export async function stepUpCrypto(email: string, masterPassword: string): Promise<string> {
  await initializeCrypto();
  const normalizedEmail = email.toLowerCase().trim();
  const authHash = await sodiumDeriveAuthHash(masterPassword, normalizedEmail);
  const b64 = toBase64(authHash);
  zeroMemory(authHash);
  return b64;
}

// ─── Login Crypto ──────────────────────────────────────────────────────────

export interface LoginCryptoResult {
  authHash: string;         // base64 — sent to server
  masterKey: Uint8Array;    // cached in memory only
}

/**
 * Perform client-side crypto for login.
 *
 * 1. Derive the auth hash (PBKDF2) to authenticate with the server
 * 2. Derive the master key (Argon2id with server-provided salt) for local decryption
 *
 * The caller MUST zero the returned masterKey after caching it.
 */
export async function loginCrypto(
  email: string,
  masterPassword: string,
  kdfSaltB64: string,
): Promise<LoginCryptoResult> {
  await initializeCrypto();
  const normalizedEmail = email.toLowerCase().trim();
  const kdfSalt = fromBase64(kdfSaltB64);

  // Derive auth hash (PBKDF2) — sent to server
  const authHash = await sodiumDeriveAuthHash(masterPassword, normalizedEmail);

  // Derive master key (Argon2id) — stays on client only
  const masterKey = await sodiumDeriveMasterKey(masterPassword, kdfSalt);

  const result: LoginCryptoResult = {
    authHash: toBase64(authHash),
    masterKey,
  };

  zeroMemory(authHash);
  return result;
}

// ─── Vault Key Decryption ──────────────────────────────────────────────────

/**
 * Decrypt a vault's DEK (Data Encryption Key) using the user's KEK.
 *
 * The vault DEK is stored encrypted (wrapped) with the user's KEK.
 * The KEK is derived from the user's master key via HKDF.
 */
export async function decryptVaultKey(
  encryptedVaultKeyB64: string,
  kekSaltB64: string,
  masterKey: Uint8Array,
): Promise<Uint8Array> {
  await initializeCrypto();
  const encryptedVaultKey = fromBase64(encryptedVaultKeyB64);
  const kekSalt = fromBase64(kekSaltB64);

  // Re-derive KEK with the stored salt
  const kek = await deriveKEKWithSalt(masterKey, 'vault-key', kekSalt);

  // Decrypt the vault DEK
  if (encryptedVaultKey.length <= 24) {
    zeroMemory(kek);
    throw new Error('Encrypted vault key is malformed');
  }
  const nonce = encryptedVaultKey.slice(0, 24);
  const ciphertext = encryptedVaultKey.slice(24);
  const vaultKey = decrypt(ciphertext, nonce, kek);

  zeroMemory(kek);
  return vaultKey;
}

/**
 * Encrypt a new vault DEK with the user's KEK (for vault creation).
 */
export async function encryptVaultKey(
  vaultKey: Uint8Array,
  masterKey: Uint8Array,
): Promise<{ encryptedVaultKey: string; kekSalt: string }> {
  await initializeCrypto();
  const { key: kek, salt: kekSalt } = await deriveKEK(masterKey, 'vault-key');

  const encrypted = await encrypt(vaultKey, kek);
  zeroMemory(kek);

  return {
    encryptedVaultKey: toBase64(new Uint8Array([...encrypted.nonce, ...encrypted.ciphertext])),
    kekSalt: toBase64(kekSalt),
  };
}

// ─── Item Encryption / Decryption ──────────────────────────────────────────

export interface EncryptedItemPayload {
  name: { ciphertext: string; nonce: string; schemeVersion: number };
  encryptedFields: Record<string, { ciphertext: string; nonce: string; schemeVersion: number }>;
  wrappedDek: { ciphertext: string; nonce: string; schemeVersion: number };
  kekSalt: string;
}

/**
 * Encrypt all fields of a vault item with a fresh DEK.
 * The DEK is then wrapped (encrypted) with the user's KEK.
 *
 * @param itemData - Plaintext fields: { name, username, password, url, notes, ... }
 * @param vaultKey - The vault's DEK (used to encrypt the item's DEK)
 * @param masterKey - User's master key (used to derive KEK to wrap the item DEK)
 */
export async function encryptItem(
  itemData: Record<string, string>,
  vaultKey: Uint8Array,
  _masterKey: Uint8Array,
): Promise<EncryptedItemPayload> {
  await initializeCrypto();
  // Generate a fresh DEK for this item
  const itemDek = await generateDEK();

  // Encrypt each field with the item DEK
  const encryptedFields: Record<string, { ciphertext: string; nonce: string; schemeVersion: number }> = {};

  for (const [fieldName, fieldValue] of Object.entries(itemData)) {
    if (fieldValue === undefined || fieldValue === null) continue;
    const encrypted = await encrypt(
      new TextEncoder().encode(fieldValue),
      itemDek,
    );
    encryptedFields[fieldName] = {
      ciphertext: toBase64(encrypted.ciphertext),
      nonce: toBase64(encrypted.nonce),
      schemeVersion: 1,
    };
  }

  // Encrypt the name separately (for search indexing on the server side)
  const encName = await encrypt(
    new TextEncoder().encode(itemData.name || ''),
    itemDek,
  );

  // Wrap the item DEK with a KEK derived from the vault key
  // The vault key acts as a shared secret for all items in a vault
  const { key: kek, salt: kekSalt } = await deriveKEK(
    new Uint8Array([...vaultKey]),
    'item-key',
  );
  const wrappedDek = await encrypt(itemDek, kek);

  zeroMemory(itemDek);
  zeroMemory(kek);

  return {
    name: {
      ciphertext: toBase64(encName.ciphertext),
      nonce: toBase64(encName.nonce),
      schemeVersion: 1,
    },
    encryptedFields,
    wrappedDek: {
      ciphertext: toBase64(wrappedDek.ciphertext),
      nonce: toBase64(wrappedDek.nonce),
      schemeVersion: 1,
    },
    kekSalt: toBase64(kekSalt),
  };
}

/**
 * Decrypt all fields of a vault item.
 *
 * @param encryptedItem - The encrypted item from the API
 * @param vaultKey - The vault's DEK
 * @param masterKey - User's master key
 */
export async function decryptItem(
  encryptedItem: EncryptedItemPayload,
  vaultKey: Uint8Array,
  _masterKey: Uint8Array,
): Promise<Record<string, string>> {
  await initializeCrypto();
  // Re-derive KEK to unwrap the item DEK
  const kekSalt = fromBase64(encryptedItem.kekSalt);
  const kek = await deriveKEKWithSalt(
    new Uint8Array([...vaultKey]),
    'item-key',
    kekSalt,
  );

  // Unwrap the item DEK
  const wrappedDekCiphertext = fromBase64(encryptedItem.wrappedDek.ciphertext);
  const wrappedDekNonce = fromBase64(encryptedItem.wrappedDek.nonce);
  const itemDek = await decrypt(wrappedDekCiphertext, wrappedDekNonce, kek);

  zeroMemory(kek);

  // Decrypt each field
  const result: Record<string, string> = {};

  for (const [fieldName, encField] of Object.entries(encryptedItem.encryptedFields)) {
    try {
      const ciphertext = fromBase64(encField.ciphertext);
      const nonce = fromBase64(encField.nonce);
      const plaintext = await decrypt(ciphertext, nonce, itemDek);
      result[fieldName] = new TextDecoder().decode(plaintext);
    } catch {
      result[fieldName] = '[decryption failed]';
    }
  }

  // Decrypt the name
  try {
    const nameCiphertext = fromBase64(encryptedItem.name.ciphertext);
    const nameNonce = fromBase64(encryptedItem.name.nonce);
    const namePlaintext = await decrypt(nameCiphertext, nameNonce, itemDek);
    result.name = new TextDecoder().decode(namePlaintext);
  } catch {
    result.name = '[decryption failed]';
  }

  zeroMemory(itemDek);
  return result;
}

// ─── Sharing (ECDH Envelope Encryption) ────────────────────────────────────

/**
 * Encrypt an item's DEK for sharing with another user.
 *
 * Uses ECDH key agreement + envelope encryption:
 * 1. Compute shared secret from sender's private key + recipient's public key
 * 2. Encrypt the item DEK with the shared key
 * 3. Sign the envelope with sender's Ed25519 key
 */
export async function encryptForShare(
  itemKey: Uint8Array,
  recipientPublicKeyX25519: Uint8Array,
  senderPrivateKeyX25519: Uint8Array,
  senderPrivateKeyEd25519: Uint8Array,
): Promise<{ encryptedItemKey: string; nonce: string }> {
  await initializeCrypto();
  const envelope = await encryptEnvelope(
    itemKey,
    recipientPublicKeyX25519,
    senderPrivateKeyX25519,
    senderPrivateKeyEd25519,
  );

  return {
    encryptedItemKey: btoa(JSON.stringify({
      dataCiphertext: toBase64(envelope.dataCiphertext),
      dataNonce: toBase64(envelope.dataNonce),
      wrappedKey: toBase64(envelope.wrappedKey),
      wrappedKeyNonce: toBase64(envelope.wrappedKeyNonce),
      signature: toBase64(envelope.signature),
    })),
    nonce: toBase64(envelope.dataNonce),
  };
}

/**
 * Decrypt a shared item's DEK.
 *
 * Reverses the ECDH envelope encryption from the sender.
 */
export async function decryptFromShare(
  encryptedItemKeyB64: string,
  nonceB64: string,
  senderPublicKeyX25519: Uint8Array,
  recipientPrivateKeyX25519: Uint8Array,
  senderPublicKeyEd25519: Uint8Array,
): Promise<Uint8Array> {
  await initializeCrypto();
  void nonceB64;
  const serialized = JSON.parse(atob(encryptedItemKeyB64)) as Record<string, string>;
  const envelope = {
    dataCiphertext: fromBase64(serialized.dataCiphertext),
    dataNonce: fromBase64(serialized.dataNonce),
    wrappedKey: fromBase64(serialized.wrappedKey),
    wrappedKeyNonce: fromBase64(serialized.wrappedKeyNonce),
    signature: fromBase64(serialized.signature),
  };

  return decryptEnvelope(
    envelope,
    senderPublicKeyEd25519,
    recipientPrivateKeyX25519,
    senderPublicKeyX25519,
  );
}

// ─── Unlock (Re-derive Master Key) ─────────────────────────────────────────

/**
 * Re-derive the master key from password for vault unlock.
 * Used when the vault is locked (e.g., after idle timeout).
 */
export async function unlockCrypto(
  masterPassword: string,
  kdfSaltB64: string,
): Promise<Uint8Array> {
  await initializeCrypto();
  const kdfSalt = fromBase64(kdfSaltB64);
  return sodiumDeriveMasterKey(masterPassword, kdfSalt);
}
